import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom';
import { FontSize } from '../../constants/type-constants';
import { useStore } from '../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import RoutesConstants from '../../shared-components/routes/routes-constants';
import { formatMessage } from '../../translations/format-message';
import PrintContent from './components/print-content';
import RemoveTask from './components/remove-task';
import PrintContentPopupHOC from './hoc/print-popup-hoc';
import RemoveTaskPopupHOC from './hoc/remove-task-popup-hoc';
import ModeSelectionModal from './components/mode-selection-modal';
import './operator.css';
import { ProcessTypeEnum } from '../../constants/enums';
import { IFileListItem } from '../../models/response/IOperatorAssignedTaskResponse';
import OperatorAssignSingleMode from './modes/operator-assign-single-mode';
import OperatorAssignBatchMode from './modes/operator-assign-batch-mode';
import { useOperatorAssignSingle } from './OperatorAssignSingle';
import { useOperatorAssignBatch } from './OperatorAssignBatch';
import config from '../../helpers/config-helper';
import worker_script from './../../shared-components/web-worker/webWorker';
import ProcessInUseConfirmPopup from './components/process-in-use-confirm-popup';
import ProcessPauseConfirmPopup from './components/process-pause-confirm-popup';

const appConfig = config();
const showAddTrayInput = appConfig.REACT_APP_SHOW_ADD_TRAY_INPUT;
const EMPTY_FILE_LIST: IFileListItem[] = [];
const timerWorker = new Worker(worker_script);

export const parseDimensionValue = (rawValue: any) => {
  let value =
    rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
  let isDefaultChecked = false;
  const lastIndex = value.lastIndexOf('$');

  if (lastIndex !== -1) {
    const suffix = value.substring(lastIndex + 1);
    if (suffix === 'true' || suffix === 'false') {
      isDefaultChecked = suffix === 'true';
      value = value.substring(0, lastIndex).trim();
    }
  }

  return {
    value,
    hasValue: value !== '' && value !== '-' && value !== '--',
    isDefaultChecked,
  };
};

export const getDimensionCheckKey = (
  tabIndex: number,
  rowIndex: number,
  dimId: number
) => `${tabIndex}-${rowIndex}-${dimId}`;

export const getLegacyDimensionCheckKey = (rowIndex: number, dimId: number) =>
  `${rowIndex}-${dimId}`;

export const getEffectiveDimensionChecked = (
  dimensionChecks: Record<string, boolean>,
  tabIndex: number,
  rowIndex: number,
  dimId: number,
  isDefaultChecked: boolean
) => {
  const scopedKey = getDimensionCheckKey(tabIndex, rowIndex, dimId);
  const legacyKey = getLegacyDimensionCheckKey(rowIndex, dimId);

  if (dimensionChecks[scopedKey] !== undefined) {
    return dimensionChecks[scopedKey];
  }

  if (dimensionChecks[legacyKey] !== undefined) {
    return dimensionChecks[legacyKey];
  }

  return isDefaultChecked;
};

export const buildCheckItemsPayloadForFileList = (
  fileList: any[],
  checkItemsState: any
) => {
  if (!fileList || !fileList.length) return [];

  return fileList.flatMap((fileItem: any, fileIndex: number) => {
    const tabChecks = checkItemsState?.[fileIndex] ?? {};

    return (fileItem?.CheckItems ?? []).flatMap((group: any) => {
      return (group?.CheckItemMessage ?? [])
        .filter((item: any) => item?.Id != null)
        .map((item: any) => {
          const stateData = tabChecks[item.Id] ?? {};

          return {
            Id: item.Id,
            IsChecked:
              stateData.isChecked !== undefined
                ? stateData.isChecked
                : (item.IsChecked ?? false),
            InputMessage:
              stateData.InputMessage !== undefined
                ? stateData.InputMessage
                : (item.InputMessage ?? ''),
          };
        });
    });
  });
};

export const buildDimensionPayloadForFileList = (
  fileList: any[],
  dimensionChecksState: Record<string, boolean>
) => {
  if (!fileList || !fileList.length) return [];

  return fileList.flatMap((file: any, fileIndex: number) => {
    const dimensions = file?.DimensionsData ?? [];

    return dimensions
      .filter((dim: any) => dim.DimensionType === 1)
      .map((dim: any) => {
        const valuesArray = (dim.DimensionValues ?? []).map(
          (rawValue: any, rowIndex: number) => {
            let value =
              rawValue === null || rawValue === undefined
                ? ''
                : String(rawValue);
            let isDefaultChecked = false;
            const lastIndex = value.lastIndexOf('$');

            if (lastIndex !== -1) {
              const suffix = value.substring(lastIndex + 1);
              if (suffix === 'true' || suffix === 'false') {
                isDefaultChecked = suffix === 'true';
                value = value.substring(0, lastIndex);
              }
            }

            if (value.trim() === '') {
              value = '-';
            }

            const checked = getEffectiveDimensionChecked(
              dimensionChecksState,
              fileIndex,
              rowIndex,
              dim.Id,
              isDefaultChecked
            );
            return `${value}$${checked}`;
          }
        );

        return {
          Id: dim.Id,
          DimensionValue: valuesArray,
        };
      });
  });
};

export const buildMemoPayloadForFileList = (
  fileList: any[],
  memoState: Record<number, string>,
  trayId?: string | null
) => {
  if (!fileList || !fileList.length) return [];

  return fileList.map((fileItem: any, index: number) => {
    const memoKey = fileItem?.TabIndex ?? index;
    const existingMemo =
      Array.isArray(fileItem?.MemoMessage) && fileItem.MemoMessage.length > 0
        ? fileItem.MemoMessage[0]
        : null;

    return {
      Id: Number(existingMemo?.Id ?? 0),
      TrayId: trayId?.trim(),
      TabIndex: Number(memoKey),
      Memo: memoState?.[memoKey] ?? existingMemo?.Memo ?? '',
    };
  });
};

const OperatorAssignTask1 = (props: any) => {
  const { operatorStore, preferencesStore } = useStore();
  const {
    CompleteProcessService,
    completeProcessState,
    GetOperatorService,
    getOperatorState,
    OperatorResponse,
    resetCompleteProcessState,
    resetOperatorResponse,
    removeForAbnormalityState,
    resetRemoveForAbnormalityState,
    operatorTeplarDetails,
    resetGetOperatorState,
    UpdateProcessService,
    updateProcessState,
    resetUpdateProcessState,
    bomDataList,
    SaveTeplarOrderService,
    saveTeplarOrderState,
    resetSaveTeplarOrderState,
    CheckProcessStatus,
    checkProcessStatusState,
    resetCheckProcessStatusState,
    inProgressOrCompletedTrays,
    completedTrays,
    isPaused,
    isTimerRunning,
    setIsTimerRunning,
  } = operatorStore;
  const { language } = preferencesStore;
  const { taskId } = useParams<any>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const processName = queryParams.get('processName');
  const processType = ProcessTypeEnum[queryParams.get('processType')];
  const [operatorCount, setOperatorCount] = useState<number>(1);
  const [isBatch, setIsBatch] = useState<boolean | null>(null);
  const [startDateTime, setStartDateTime] = useState(null);
  const [timer, setTimer] = useState<number>(0);
  const [isProcessStarted, setIsProcessStarted] = useState(false);
  const [showDraggableStart, setShowDraggableStart] = useState(true);
  // Scan buffer state for single mode
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastKeyTime, setLastKeyTime] = useState(Date.now());
  const [trayIndex, setTrayIndex] = useState(0);
  const [trayHistory, setTrayHistory] = useState<string[]>([]);
  const [selectedTrayId, setSelectedTrayId] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [trayId, setTrayId] = useState(''); //remove later
  const [printContents, setPrintContents] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState<string>('');
  const [startTimer, setStartTimer] = useState<number>(0);
  const [removeDuration, setRemoveDuration] = useState<number>(0);
  const [dimensionChecks, setDimensionChecks] = useState<
    Record<string, boolean>
  >({});
  const [memo, setMemo] = useState<Record<number, string>>({});
  const [webWorkerTime, setWebWorkerTime] = useState(0);
  const [showModeSelectionPopup, setShowModeSelectionPopup] = useState(false);
  const [teplarOrderData, setTeplarOrderData] = useState<
    { Id: number; Order: number }[]
  >([]);
  const [saveClicked, setSaveClicked] = useState(false);
  const traySelectRef = useRef<HTMLButtonElement>(null);

  type CheckItemState = {
    isChecked?: boolean;
    InputMessage?: string;
  };

  type CheckState = {
    [uiTabIndex: number]: {
      [checkItemId: number]: CheckItemState;
    };
  };

  const [submittedCheckItems, setSubmittedCheckItems] = useState<CheckState>(
    {}
  );
  const [canvasDrawings, setCanvasDrawings] = useState<Record<string, string>>(
    {}
  );

  const [bomDataListState, setBomDataListState] = useState<any[]>([]);

  const [teplarList, setTeplarList] = useState<any[]>([]);
  const [isAddTrayInput, setIsAddTrayInput] = useState(false);
  const [isRemoveForAbnormalityClicked, setIsRemoveForAbnormalityClicked] =
    useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCompletedProcessErrorModal, setShowCompletedProcessErrorModal] =
    useState(false);
  const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
  const [showStart, setShowStart] = useState(false);

  const productImages: IFileListItem[] =
    OperatorResponse.FileList ?? EMPTY_FILE_LIST;
  // Calculate the correct FileList index (subtract 1 because mergedTabs includes dimension table at index 0)
  const fileListIndex = activeIndex > 0 ? activeIndex - 1 : -1;

  const checkItems: [] =
    OperatorResponse.FileList && activeIndex >= 0
      ? OperatorResponse.FileList[activeIndex]?.CheckItems
      : [];

  const dims = OperatorResponse?.FileList?.[activeIndex]?.DimensionsData ?? [];

  const dimensionTable = (() => {
    const maxValueRows = Math.max(
      ...dims.map((d) => d.DimensionValues?.length ?? 0),
      0
    );

    // ✅ if headers exist but no values → show one "--" row
    const totalRows = dims.length > 0 ? Math.max(maxValueRows, 1) : 0;

    return Array.from({ length: totalRows }, (_, i) => {
      const row: any = {};

      dims.forEach((dim) => {
        const hasValue =
          Array.isArray(dim.DimensionValues) && i < dim.DimensionValues.length;

        let rawValue = hasValue ? dim.DimensionValues[i] : '-';
        let val = rawValue?.toString() ?? '-';

        let isChecked = false;
        const lastIndex = val.lastIndexOf('$');

        if (lastIndex !== -1) {
          const suffix = val.substring(lastIndex + 1);
          if (suffix === 'true' || suffix === 'false') {
            isChecked = suffix === 'true';
            val = val.substring(0, lastIndex);
          }
        }

        if (val.trim() === '') {
          val = '-';
        }

        row[dim.DimensionColumn] = {
          value: val,
          isDefaultChecked: isChecked,
        };
      });

      return row;
    });
  })();

  const [printCount, setPrintCount] = useState<number[]>([]);

  const history = useHistory();

  // Decrement operator count (minimum 0)
  const handleDecrement = () => {
    setOperatorCount((prev) => Math.max(0, prev - 1));
  };

  // Increment operator count
  const handleIncrement = () => {
    setOperatorCount((prev) => prev + 1);
  };

  // Update operator count from numeric input
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setOperatorCount(value);
    }
  };

  // Increment print copy count for item index
  const handleIncrementPrintCount = (index: number) => {
    setPrintCount((prev) => {
      const newPrintCount = [...prev];
      newPrintCount[index] = (newPrintCount[index] || 0) + 1;
      return newPrintCount;
    });
  };

  // Decrement print copy count for item index
  const handleDecrementPrintCount = (index: number) => {
    setPrintCount((prev) => {
      const newPrintCount = [...prev];
      newPrintCount[index] = (newPrintCount[index] || 0) - 1;
      return newPrintCount;
    });
  };

  useEffect(() => {
    timerWorker.onmessage = ({ data: { time } }) => {
      setWebWorkerTime(time);
    };
  }, []);

  // Start background worker timer
  const startWebWorkerTimer = (initialTime = webWorkerTime) => {
    timerWorker.postMessage({ turn: 'on', initialTime });
  };

  // Stop background worker timer
  const stopWebWorkerTimer = () => {
    timerWorker.postMessage({ turn: 'off' });
  };

  // Reset background worker timer to zero
  const resetWebWorkerTimer = () => {
    timerWorker.postMessage({ turn: 'reset' });
    setWebWorkerTime(0);
  };

  useEffect(() => {
    if (operatorTeplarDetails.length > 0) {
      let arr = Array.from({ length: length }, (_, index) => 1);
      setPrintCount(arr);
    }
  }, [operatorTeplarDetails]);

  // Set print copy count for item index from input
  const handleChangePrintCount = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = parseInt(e.target.value);
    setPrintCount((prev) => {
      const newPrintCount = [...prev];
      newPrintCount[index] = value;
      return newPrintCount;
    });
  };

  // Convert text string to hex format using TextEncoder
  const stringToHex = (text) => {
    var bytes = new TextEncoder().encode(text);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
      ''
    );
  };

  // Submit tray Teplar order data to backend service
  const handleSaveTeplarOrder = (
    trayTeplarOrders?: { Id: number; Order: number }[]
  ) => {
    const resolvedTrayTeplarOrders = trayTeplarOrders ?? teplarOrderData;

    if (resolvedTrayTeplarOrders.length === 0) {
      return Promise.resolve();
    }

    const payload = {
      TenantId: parseInt(getTenantIdFromLocalStorage(), 10),
      TrayTeplarOrders: resolvedTrayTeplarOrders,
    };
    return SaveTeplarOrderService(payload);
  };

  const bulidCheckItemsPayload = () => {
    const fileList = OperatorResponse?.FileList ?? [];
    return buildCheckItemsPayloadForFileList(fileList, submittedCheckItems);
  };

  const buildMemoPayload = () => {
    const fileList = OperatorResponse?.FileList ?? [];
    return buildMemoPayloadForFileList(fileList, memo, selectedTrayId);
  };

  const buildDimensionPayload = () => {
    const fileList = OperatorResponse?.FileList ?? [];
    return buildDimensionPayloadForFileList(fileList, dimensionChecks);
  };

  // Build payload array for BOM checklist items
  const buildBOMCheckListPayload = () => {
    return bomDataListState.map((item) => ({
      Id: item.Id,
      Pick: item.Pick ?? false,
      Shortage: item.Shortage ?? false,
    }));
  };

  // Build payload array for Teplar print status
  const buildTeplarListPayload = () =>
    teplarList.map((item) => ({
      Id: item.Id,
      IsPrinted: Boolean(item.isPrinted || item.Printed),
    }));

  useEffect(() => {
    setShowModeSelectionPopup(true);
    localStorage.removeItem('trayData');
  }, []);

  useEffect(() => {
    if (isBatch !== null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        toast.error(formatMessage('please_select_mode_before_scan'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isBatch]);

  // Convert seconds integer to HH:MM:SS format string
  const formatTime = (seconds: number) => {
    const totalSeconds = Math.floor(seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hrs, mins, secs].map((v) => String(v).padStart(2, '0')).join(':');
  };

  // Map font size name to CSS class string
  const getFontSizeClass = (size: FontSize) => {
    if (size === 'small') return 'opertor-small-text';
    if (size === 'large') return 'opertor-text-large';
    return 'opertor-normal-text';
  };
  const savedFontSize = localStorage.getItem('fontSize') as FontSize;
  const initialFontSize: FontSize =
    savedFontSize === 'small' || savedFontSize === 'large'
      ? savedFontSize
      : 'normal';

  const [fontSizeClass, setFontSizeClass] = useState<string>(
    getFontSizeClass(initialFontSize)
  );
  const [activeSize, setActiveSize] = useState<FontSize>(initialFontSize);

  // Update font size setting state and save preference to localStorage
  const handleFontSizeChange = (size: FontSize) => {
    localStorage.setItem('fontSize', size);
    setActiveSize(size);

    setFontSizeClass(getFontSizeClass(size));
  };

  // Update memo text value for specified tab index
  const handleMemoChange = (tabIndex: number, value: string) => {
    setMemo((prev) => ({
      ...prev,
      [tabIndex]: value,
    }));
  };

  // Update dimension checkbox state using composite tab-row-dimId key
  const handleDimensionCheck = (
    tabIndex: number,
    rowIndex: number,
    dimId: number,
    checked: boolean
  ) => {
    const key = getDimensionCheckKey(tabIndex, rowIndex, dimId);
    setDimensionChecks((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  // Store canvas drawing image data URL for specified key
  const handleCanvasChange = (key: string, dataUrl: string) => {
    setCanvasDrawings((prev) => ({
      ...prev,
      [key]: dataUrl,
    }));
  };

  useEffect(() => {
    resetWebWorkerTimer();
    resetOperatorResponse();
  }, []);

  useEffect(() => {
    if (updateProcessState.success) {
      if (saveClicked) {
        toast.success(formatMessage('updated_success'));
      }
      // activeMode.handleStartTimer();
      setSaveClicked(false);
      resetUpdateProcessState();
    }
  }, [updateProcessState.success]);

  useEffect(() => {
    if (updateProcessState.error) {
      // props.confirmToggleHandler();
      // if(updateProcessState.error === "ProcessInUse") { //Take String to some const
      //   setShowConfirmModal(true);//<-- this will use ErrorMessageData
      // }
      // toast.error(formatMessage(updateProcessState.error));
      resetUpdateProcessState();
      //ARIF: ErrorMessageData = ""
    }
  }, [updateProcessState.error]);

  useEffect(() => {
    if (checkProcessStatusState.success) {
      // if (saveClicked) {
      //   toast.success(formatMessage('updated_success'));
      // }
      activeMode.handleStartTimer();
      // setSaveClicked(false);
      resetCheckProcessStatusState();
    }
  }, [checkProcessStatusState.success]);

  useEffect(() => {
    if (checkProcessStatusState.error) {
      // props.confirmToggleHandler();
      if (checkProcessStatusState.error === 'ProcessAlreadyCompleted') {
        setShowCompletedProcessErrorModal(true);
        setShowConfirmModal(false);
      } else if (checkProcessStatusState.error === 'ProcessInUse') {
        //Take String to some const
        setShowConfirmModal(true); //<-- this will use ErrorMessageData
        setShowCompletedProcessErrorModal(false);
      }
      // toast.error(formatMessage(checkProcessStatusState.error));
      resetCheckProcessStatusState();
      //ARIF: ErrorMessageData = ""
    }
  }, [checkProcessStatusState.error]);

  const mergedTabs = useMemo(() => {
    const tabs = [];
    const isTrue = (val: any) =>
      val === true || String(val).toLowerCase() === 'true';

    // if (dimensionTable.length > 0) {
    // tabs.push({
    //   type: 'dimension',
    //   Heading: formatMessage('dimension_table'),
    // });
    // }
    productImages.forEach((img, i) => {
      const isDimensionTab =
        isTrue(img?.IsDimension) || isTrue(img?.isDimension);
      tabs.push({
        type: 'image',
        Heading: isDimensionTab
          ? formatMessage('dimension_table')
          : img?.FileDetail[0]?.Heading || formatMessage('dimension_table'),
        data: img,
        originalIndex: i,
      });
    });

    return tabs;
  }, [productImages, language]);

  const areAllTabsChecked = useMemo(() => {
    let allCheckItemsChecked = true;
    let allDimensionValuesChecked = true;
    let hasAnyDimensionCheckbox = false;
    mergedTabs.forEach((tab, uiTabIndex) => {
      const data = tab.data;
      const checkItems = data?.CheckItems ?? [];
      if (checkItems.length > 0) {
        const tabChecks = submittedCheckItems[uiTabIndex] || {};
        // const allChecked = checkItems.every(
        //   (item: any) => tabChecks[item.Id]?.isChecked === true
        // );
        const allChecked = checkItems.every((item: any) =>
          (item.CheckItemMessage ?? []).every((msg: any) => {
            const checked =
              tabChecks[msg.Id]?.isChecked !== undefined
                ? tabChecks[msg.Id]?.isChecked
                : (msg.IsChecked ?? false);
            return checked === true;
          })
        );
        if (!allChecked) {
          allCheckItemsChecked = false;
        }
      }
      const dimensionsData = data?.DimensionsData ?? [];
      dimensionsData.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;
        const values = dim.DimensionValues ?? [];
        const realValueRowIndices: number[] = [];
        values.forEach((v: any, idx: number) => {
          const { hasValue } = parseDimensionValue(v);
          if (hasValue) {
            realValueRowIndices.push(idx);
          }
        });
        if (realValueRowIndices.length === 0) return;
        hasAnyDimensionCheckbox = true;
        realValueRowIndices.forEach((rowIndex: number) => {
          const { isDefaultChecked } = parseDimensionValue(values[rowIndex]);
          const checked = getEffectiveDimensionChecked(
            dimensionChecks,
            uiTabIndex,
            rowIndex,
            dim.Id,
            isDefaultChecked
          );
          if (!checked) {
            allDimensionValuesChecked = false;
          }
        });
      });
    });
    return (
      allCheckItemsChecked &&
      (!hasAnyDimensionCheckbox || allDimensionValuesChecked)
    );
  }, [mergedTabs, submittedCheckItems, dimensionChecks]);

  // Include BOM validation: when processType is BOM (3), require each BOM row to have Shortage or Pick checked
  const isBOMValidated = useMemo(() => {
    try {
      if (Number(processType) !== 3) return true;
      if (!bomDataListState || bomDataListState.length === 0) return true;
      return bomDataListState.every(
        (row: any) => !!row && (row.Shortage === true || row.Pick === true)
      );
    } catch (e) {
      return true;
    }
  }, [processType, bomDataListState]);

  const tabValidationMap = useMemo(() => {
    const validationMap: Record<number, boolean> = {};

    mergedTabs.forEach((tab, uiTabIndex) => {
      const data = tab.data;
      const checkItems = data?.CheckItems ?? [];
      const tabChecks = submittedCheckItems[uiTabIndex] || {};
      const allCheckItemsChecked =
        checkItems.length === 0 ||
        checkItems.every((item: any) =>
          (item.CheckItemMessage ?? []).every((msg: any) => {
            const checked =
              tabChecks[msg.Id]?.isChecked !== undefined
                ? tabChecks[msg.Id]?.isChecked
                : (msg.IsChecked ?? false);
            return checked === true;
          })
        );

      let allDimensionValuesChecked = true;
      const dimensionsData = data?.DimensionsData ?? [];
      dimensionsData.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;

        (dim.DimensionValues ?? []).forEach((value: any, rowIndex: number) => {
          const { hasValue, isDefaultChecked } = parseDimensionValue(value);
          if (!hasValue) return;

          const checked = getEffectiveDimensionChecked(
            dimensionChecks,
            uiTabIndex,
            rowIndex,
            dim.Id,
            isDefaultChecked
          );
          if (!checked) {
            allDimensionValuesChecked = false;
          }
        });
      });

      validationMap[uiTabIndex] =
        allCheckItemsChecked && allDimensionValuesChecked && isBOMValidated;
    });

    return validationMap;
  }, [mergedTabs, submittedCheckItems, dimensionChecks, isBOMValidated]);

  const singleMode = useOperatorAssignSingle({
    enabled: isBatch === false,
    taskId,
    processType,
    OperatorResponse,
    getOperatorState,
    resetOperatorResponse,
    resetCompleteProcessState,
    completeProcessState,
    removeForAbnormalityState,
    resetRemoveForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder: handleSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    setShowDraggableStart,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    scanBuffer,
    setScanBuffer,
    lastKeyTime,
    setLastKeyTime,
    traySelectRef,
    removeTaskToggleHandler: props.removeTaskToggleHandler,
    setRemoveDuration,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    submittedCheckItems,
    memo,
    dimensionChecks,
    canvasDrawings,
    bomDataListState,
    resetGetOperatorState,
    buildTeplarListPayload,
    teplarList,
    setTeplarList,
    isRemoveForAbnormalityClicked,
    setIsRemoveForAbnormalityClicked,
    saveClicked,
    setSaveClicked,
    updateProcessState,
    showConfirmModal,
    CheckProcessStatus,
    checkProcessStatusState,
  });

  const batchMode = useOperatorAssignBatch({
    enabled: isBatch === true,
    taskId,
    processType,
    OperatorResponse,
    removeForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder: handleSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    // Batch mode manages scan buffer state via internal refs
    traySelectRef,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    submittedCheckItems,
    memo,
    dimensionChecks,
    canvasDrawings,
    bomDataListState,
    setShowDraggableStart,
    resetOperatorResponse,
    buildTeplarListPayload,
    teplarList,
    setTeplarList,
    getOperatorState,
    resetGetOperatorState,
    isAddTrayInput,
    setIsAddTrayInput,
    operatorTeplarDetails,
    updateProcessState,
    showConfirmModal,
    CheckProcessStatus,
    checkProcessStatusState,
  });

  const activeMode = isBatch ? batchMode : singleMode;

  useEffect(() => {
    if (saveTeplarOrderState.success) {
      if (saveClicked) {
        toast.success(formatMessage('updated_success'));
      }
      setSaveClicked(false);
      resetSaveTeplarOrderState();
    }
  }, [saveTeplarOrderState.success, saveClicked]);

  useEffect(() => {
    if (saveTeplarOrderState.error) {
      toast.error(formatMessage(saveTeplarOrderState.error));
      setSaveClicked(false);
      resetSaveTeplarOrderState();
    }
  }, [saveTeplarOrderState.error]);

  const infoFields = [
    {
      labelKey: 'product_id',
      value: OperatorResponse.ProductName,
      colWidth: 1,
    },
    {
      labelKey: 'kishu',
      value:
        `${OperatorResponse?.KishuMark || ''} ${OperatorResponse?.Kishu || ''}`.trim(),
      colWidth: 2,
    },
    {
      labelKey: 'katashiki',
      value: OperatorResponse.Katashiki1 + OperatorResponse.Katashiki2,
      colWidth: 6,
    },
    {
      labelKey: 'quantity',
      value: OperatorResponse.Quantity,
      colWidth: 1,
    },
    {
      labelKey: 'end_user_order_id',
      value: OperatorResponse.EndUserOrderId,
      colWidth: 2,
    },
  ];

  // Pause timer, clear operator response state, and navigate back to task list
  const handleBackClick = async () => {
    if (trayHistory.length > 0 && isTimerRunning) {
      activeMode.handlePauseTimer();
    }

    await resetOperatorResponse();
    await resetCompleteProcessState();

    history.push(RoutesConstants.OperatorListDetail);
  };

  // Update text input message for specified check item ID
  const handleInfoChange = (
    stepIndex: number,
    itemId: number,
    value: string
  ) => {
    setSubmittedCheckItems((prev) => ({
      ...prev,
      [stepIndex]: {
        ...(prev[stepIndex] || {}),
        [itemId]: {
          ...(prev[stepIndex]?.[itemId] || {}),
          InputMessage: value,
        },
      },
    }));
  };

  const validFiles = (productImages[activeIndex]?.FileDetail ?? []).filter(
    (file) => file.S3FilePath !== null
  );

  // Close abnormality modal and resume timer worker if process was active
  const closeRemoveForAbnormality = () => {
    if (isRemoveForAbnormalityClicked) {
      setIsTimerRunning(true);
      setIsProcessStarted(true);
      startWebWorkerTimer();
    }
    props.removeTaskToggleHandler();
  };

  const allFilesAreNull =
    productImages &&
    productImages.length > 0 &&
    dims.length === 0 &&
    validFiles.length === 0;

  // Display toast notification when LED blinking trigger is clicked
  const openLEDHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    toast.success(formatMessage('led_blinking'));
    event.stopPropagation();
  };

  const viewProps = {
    operatorTeplarDetails,
    mergedTabs,
    activeIndex,
    setActiveIndex,
    setMemo,
    setSubmittedCheckItems,
    dimensionTable,
    dims,
    dimensionChecks,
    handleDimensionCheck,
    isTimerRunning,
    productImages,
    processType,
    isProcessStarted,
    handleDecrementPrintCount,
    printCount,
    handleChangePrintCount,
    handleIncrementPrintCount,
    stringToHex,
    onSaveTeplarOrder: handleSaveTeplarOrder,
    checkItems,
    submittedCheckItems,
    handleMemoChange,
    memo,
    handleInfoChange,
    validFiles,
    allFilesAreNull,
    canvasDrawings,
    handleCanvasChange,
    bomDataListState,
    setBomDataListState,
    tabValidationMap,
    showValidationWarnings: isTimerRunning,
    onTeplarOrderChange: setTeplarOrderData,
    bomDataList,
    teplarList,
    setTeplarList,
    saveClicked,
    setSaveClicked,
    showStart,
    setShowStart,
  };

  // Toggle display of process confirmation modal
  const confirmToggleHandler = () => {
    setShowConfirmModal(true);
  };

  useEffect(() => {
    if (completeProcessState.error) {
      setShowConfirmSubmitModal(true);
    }
  }, [completeProcessState.error]);

  return (
    <>
      <Prompt
        when={isTimerRunning}
        message={formatMessage('exit_warning')}
      />{' '}
      <div className={`operator-wrapper ${fontSizeClass}`}>
        {showModeSelectionPopup && (
          <ModeSelectionModal
            show={showModeSelectionPopup}
            onModalClosed={() => setShowModeSelectionPopup(false)}
            onSelectMode={(isBatch: boolean) => setIsBatch(isBatch)}
            handleBackClick={handleBackClick}
          />
        )}
        {props.showRemoveTaskPopup && (
          <RemoveTask
            id={selectedTrayId}
            modalClosed={closeRemoveForAbnormality}
            Count={1}
            ProcessId={taskId}
            startTime={startDateTime}
            Duration={removeDuration}
            NoOfOperators={operatorCount}
            CheckItemList={bulidCheckItemsPayload()}
            MemoMessage={buildMemoPayload()}
            Dimensions={buildDimensionPayload()}
            BOM={buildBOMCheckListPayload()}
            TeplarList={buildTeplarListPayload()}
          />
        )}
        {props.showPrintContentPopup && (
          <PrintContent
            id={Number(selectedTrayId)}
            modalClosed={props.printToggleHandler}
            printContents={printContents}
            templateName={templateName}
          />
        )}
        {(showConfirmModal || showCompletedProcessErrorModal) && (
          <ProcessInUseConfirmPopup
            showConfirmModal={showConfirmModal}
            setShowConfirmModal={setShowConfirmModal}
            handleStartTimer={() => {
              setShowStart(false);
              activeMode.handleStartTimer();
            }}
            handleRemoveTray={() => {
              if (!isBatch && showCompletedProcessErrorModal) {
                singleMode.handleRemoveTray();
              }
              if (isBatch && showCompletedProcessErrorModal) {
                const completedSet = new Set(
                  completedTrays.map((tray) => String(tray).trim())
                );
                setTrayHistory((prev) => {
                  const updatedTrayHistory = prev.filter(
                    (tray) => !completedSet.has(String(tray).trim())
                  );
                  setSelectedTrayId(
                    updatedTrayHistory.length > 0
                      ? updatedTrayHistory[updatedTrayHistory.length - 1]
                      : null
                  );
                  return updatedTrayHistory;
                });

                // batchMode.handleRemoveTray();
              }
              setShowConfirmModal(false);
              setShowCompletedProcessErrorModal(false);
              if (isBatch) {
                setIsTimerRunning(false);
                setTimer(0);
                resetWebWorkerTimer();
                setIsProcessStarted(false);
              } else {
                singleMode.handleRemoveTray();
                setShowStart(true);
                setIsTimerRunning(false);
                // setTimer(startTimer);
                // resetWebWorkerTimer();
              }
              // resetOperatorResponse();
            }}
            isLoading={false}
            title={''}
            showCompletedProcessErrorModal={showCompletedProcessErrorModal}
            setShowCompletedProcessErrorModal={
              setShowCompletedProcessErrorModal
            }
            message={
              showConfirmModal
                ? formatMessage('process_in_use_confirm')
                : formatMessage('process_completed_please_remove')
            }
            error={inProgressOrCompletedTrays}
            isBatch={isBatch}
          />
        )}
        {showConfirmSubmitModal && (
          <ProcessPauseConfirmPopup
            showConfirmSubmitModal={showConfirmSubmitModal}
            setShowConfirmSubmitModal={setShowConfirmSubmitModal}
            handleRemoveTray={() => {
              if (isBatch) {
                const completedSet = new Set(
                  completedTrays.map((tray) => String(tray).trim())
                );
                setTrayHistory((prev) => {
                  const updatedTrayHistory = prev.filter(
                    (tray) => !completedSet.has(String(tray).trim())
                  );
                  setSelectedTrayId(
                    updatedTrayHistory.length > 0
                      ? updatedTrayHistory[updatedTrayHistory.length - 1]
                      : null
                  );
                  return updatedTrayHistory;
                });
              } else {
                activeMode.handleRemoveTray();
              }
              if (isPaused) {
                activeMode.PausedSuccess();
              } else {
                activeMode.completeSuccess();
              }
              resetCompleteProcessState();
            }}
            isLoading={false}
            title={''}
            message={formatMessage('trays_owned_by_other_users')}
            error={inProgressOrCompletedTrays}
            isBatch={false}
          />
        )}
        {(completeProcessState.inProgress ||
          getOperatorState.inProgress ||
          removeForAbnormalityState.inProgress ||
          updateProcessState.inProgress ||
          checkProcessStatusState.inProgress) && (
          <FormLoader
            loading={
              completeProcessState.inProgress ||
              getOperatorState.inProgress ||
              removeForAbnormalityState.inProgress ||
              updateProcessState.inProgress ||
              checkProcessStatusState.inProgress
            }
          />
        )}
        {isBatch === null ? null : isBatch ? (
          <OperatorAssignBatchMode
            processName={processName}
            handleBackClick={handleBackClick}
            trayHistory={trayHistory}
            selectedTrayId={selectedTrayId}
            shouldShowTrayWarning={activeMode.shouldShowTrayWarning}
            handleTraySelectChange={activeMode.handleTraySelectChange}
            showAddTrayInput={showAddTrayInput}
            trayId={trayId}
            setTrayId={setTrayId}
            addTray={() => {
              activeMode.addTray(trayId);
              setTrayId('');
            }}
            activeSize={activeSize}
            handleFontSizeChange={handleFontSizeChange}
            infoFields={infoFields}
            operatorResponseViewType={OperatorResponse.ViewType}
            viewProps={viewProps}
            isProcessStarted={isProcessStarted}
            operatorCount={operatorCount}
            handleDecrement={handleDecrement}
            handleIncrement={handleIncrement}
            handleChange={handleChange}
            formattedTime={formatTime(webWorkerTime)}
            buttons={activeMode.buttons}
            traySelectRef={traySelectRef}
            isTimerRunning={isTimerRunning}
            showDraggableStart={showDraggableStart}
            setShowDraggableStart={setShowDraggableStart}
            shouldShowCheckMark={activeMode.shouldShowCheckMark}
            isAddTrayInput={isAddTrayInput}
            setIsAddTrayInput={setIsAddTrayInput}
          />
        ) : (
          <OperatorAssignSingleMode
            processName={processName}
            handleBackClick={handleBackClick}
            trayHistory={trayHistory}
            selectedTrayId={selectedTrayId}
            shouldShowTrayWarning={activeMode.shouldShowTrayWarning}
            handleTraySelectChange={activeMode.handleTraySelectChange}
            showAddTrayInput={showAddTrayInput}
            trayId={trayId}
            setTrayId={setTrayId}
            addTray={() => {
              activeMode.addTray(trayId);
              setTrayId('');
            }}
            activeSize={activeSize}
            showConfirmModal={showConfirmModal}
            handleFontSizeChange={handleFontSizeChange}
            infoFields={infoFields}
            operatorResponseViewType={OperatorResponse.ViewType}
            viewProps={viewProps}
            isProcessStarted={isProcessStarted}
            operatorCount={operatorCount}
            handleDecrement={handleDecrement}
            handleIncrement={handleIncrement}
            handleChange={handleChange}
            formattedTime={formatTime(webWorkerTime)}
            buttons={activeMode.buttons}
            traySelectRef={traySelectRef}
            shouldShowCheckMark={activeMode.shouldShowCheckMark}
            // showStart={showStart}
            // setShowStart={setShowStart}
          />
        )}
      </div>
    </>
  );
};

export default PrintContentPopupHOC(
  RemoveTaskPopupHOC(observer(OperatorAssignTask1))
);
